import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, Alert } from "@mui/material";
import { Save } from "lucide-react";
import { Button } from "../../components/Button";
import { Label } from "../../components/Label";
import { CustomTextField } from "../../components/CustomTextField";
import { CustomTextArea } from "../../components/CustomTextArea";
import { UnifiedModal } from "../../components/UnifiedModal";
import { TimeLogCard } from "../../components/TimeLogCard";
import apiClient from "../../api-client";

/**
 * TimeLogModal - Modal para gestionar tiempos de tickets
 *
 * Permite:
 * - Crear un nuevo registro de tiempo (POST)
 * - Actualizar un registro existente (PATCH)
 * - Ver el tiempo actual y el balance de horas de la agencia
 */
export const TimeLogModal = ({
  isOpen,
  onClose,
  ticketId,
  ticketData = null, // Datos completos del ticket con time_log y agency_hour_balance
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Datos del formulario
  const [formData, setFormData] = useState({
    minutes_consumed: "",
    work_description: "",
  });

  // Tiempo actual registrado
  const currentTimeLog = ticketData?.time_log || null;
  const agencyBalance = ticketData?.agency_hour_balance || null;

  // Inicializar formulario cuando se abre el modal o cambian los datos
  useEffect(() => {
    if (isOpen) {
      if (currentTimeLog) {
        // Modo edición: cargar datos existentes
        setFormData({
          minutes_consumed: currentTimeLog.minutes_consumed.toString(),
          work_description: currentTimeLog.work_description || "",
        });
      } else {
        // Modo creación: valores por defecto
        setFormData({
          minutes_consumed: "",
          work_description: "",
        });
      }
      setError("");
    }
  }, [isOpen, currentTimeLog, ticketData]);

  // Formatear minutos a formato legible (ej: 90 -> "1h 30m")
  const formatMinutes = (minutes) => {
    if (minutes === null || minutes === undefined) return "0m";
    if (minutes === 0) return "0m";
    const isNegative = minutes < 0;
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    let formatted;
    if (hours > 0 && mins > 0) {
      formatted = `${hours}h ${mins}m`;
    } else if (hours > 0) {
      formatted = `${hours}h`;
    } else {
      formatted = `${mins}m`;
    }
    return isNegative ? `-${formatted}` : formatted;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const minutes = parseInt(formData.minutes_consumed, 10);

    if (!minutes || minutes <= 0) {
      setError("Los minutos deben ser un número mayor que 0");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        minutes_consumed: minutes,
        ...(formData.work_description.trim() && {
          work_description: formData.work_description.trim(),
        }),
      };

      let response;
      if (currentTimeLog) {
        // Actualizar tiempo existente (PATCH)
        response = await apiClient.patch(
          `/communication/tickets/${ticketId}/log_time/`,
          payload
        );
      } else {
        // Crear nuevo registro (POST)
        response = await apiClient.post(
          `/communication/tickets/${ticketId}/log_time/`,
          payload
        );
      }

      // Cerrar modal inmediatamente para evitar efectos de re-render
      onClose();

      // Llamar callback de éxito (mostrará SuccessPopup si corresponde)
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      console.error("Error guardando tiempo:", error);

      // Manejar diferentes tipos de errores
      if (error.response?.status === 400) {
        const errorDetail = error.response?.data?.detail || "";
        if (errorDetail.includes("Insufficient hours")) {
          setError(
            errorDetail ||
            "No hay suficientes horas disponibles en el paquete de la agencia"
          );
        } else if (errorDetail.includes("already exists")) {
          setError(
            "Ya existe un registro de tiempo para este ticket. Por favor, actualiza el existente."
          );
        } else {
          setError(
            errorDetail ||
            error.response?.data?.minutes_consumed?.[0] ||
            "Error al guardar el tiempo. Verifica los datos ingresados."
          );
        }
      } else if (error.response?.status === 403) {
        setError("No tienes permisos para gestionar tiempos de tickets");
      } else if (error.response?.status === 404) {
        setError("No se encontró el ticket o el registro de tiempo");
      } else {
        setError("Error inesperado al guardar el tiempo");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPreviewBalance = () => {
    if (!agencyBalance) return null;
    const newMinutes = parseInt(formData.minutes_consumed, 10);
    if (!newMinutes || newMinutes <= 0) return null;

    const isSameAsCurrent =
      currentTimeLog && newMinutes === currentTimeLog.minutes_consumed;
    if (isSameAsCurrent) return null;

    const baseAvailable = agencyBalance.available_minutes ?? 0;
    const baseConsumed = agencyBalance.consumed_minutes ?? 0;
    const purchasedAvailable = agencyBalance.purchased_available_minutes ?? 0;
    const purchasedConsumed = agencyBalance.purchased_consumed_minutes ?? 0;

    let adjustedBaseConsumed = baseConsumed;
    let adjustedPurchasedConsumed = purchasedConsumed;

    if (currentTimeLog) {
      let oldBase = currentTimeLog.base_minutes_consumed ?? 0;
      let oldPurchased = currentTimeLog.purchased_minutes_consumed ?? 0;
      if (
        currentTimeLog.minutes_consumed > 0 &&
        oldBase === 0 &&
        oldPurchased === 0
      ) {
        oldBase = currentTimeLog.minutes_consumed;
      }
      adjustedBaseConsumed = Math.max(0, baseConsumed - oldBase);
      adjustedPurchasedConsumed = Math.max(0, purchasedConsumed - oldPurchased);
    }

    const baseRemaining = baseAvailable - adjustedBaseConsumed;
    const purchasedRemaining = purchasedAvailable - adjustedPurchasedConsumed;

    let baseMinutes = Math.min(newMinutes, Math.max(baseRemaining, 0));
    let remaining = newMinutes - baseMinutes;
    let purchasedMinutes = Math.min(remaining, Math.max(purchasedRemaining, 0));
    remaining -= purchasedMinutes;
    if (remaining > 0) {
      baseMinutes += remaining;
    }

    const newBaseConsumed = adjustedBaseConsumed + baseMinutes;
    const newPurchasedConsumed = adjustedPurchasedConsumed + purchasedMinutes;
    const newBaseRemaining = baseAvailable - newBaseConsumed;
    const newPurchasedRemaining = purchasedAvailable - newPurchasedConsumed;

    return {
      available_minutes: baseAvailable,
      consumed_minutes: newBaseConsumed,
      purchased_available_minutes: purchasedAvailable,
      purchased_consumed_minutes: newPurchasedConsumed,
      base_remaining_minutes: newBaseRemaining,
      purchased_remaining_minutes: newPurchasedRemaining,
      remaining_minutes: newBaseRemaining + newPurchasedRemaining,
    };
  };

  const previewBalance = getPreviewBalance();
  const getTimeDelta = () => {
    if (!currentTimeLog) return null;
    const currentMinutes = parseInt(formData.minutes_consumed, 10);
    const oldMinutes = currentTimeLog.minutes_consumed;
    if (isNaN(currentMinutes)) return null;
    const delta = currentMinutes - oldMinutes;
    if (delta === 0) return null;
    return delta;
  };

  const timeDelta = getTimeDelta();

  return (
    <UnifiedModal
      open={isOpen}
      onClose={onClose}
      title={
        currentTimeLog
          ? "Editar Tiempo del Ticket"
          : "Registrar Tiempo del Ticket"
      }
      maxWidth="sm"
    >
      <Stack spacing={3}>
        {/* Tiempo actual registrado con balance de horas */}
        {(currentTimeLog || agencyBalance) && (
          <TimeLogCard
            timeLog={currentTimeLog}
            agencyBalance={agencyBalance}
            previewBalance={previewBalance}
          />
        )}

        {/* Alertas de errores */}
        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Información sobre cambio de tiempo */}
        {currentTimeLog && timeDelta !== null && (
          <Alert severity={timeDelta > 0 ? "info" : "success"}>
            {timeDelta > 0
              ? `Se añadirán ${formatMinutes(timeDelta)} al tiempo registrado`
              : `Se reducirán ${formatMinutes(
                Math.abs(timeDelta)
              )} del tiempo registrado`}
          </Alert>
        )}

        {/* Formulario */}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Minutos consumidos */}
            <Box>
              <Label sx={{ fontSize: "1rem" }}>Minutos consumidos *</Label>
              <CustomTextField
                type="number"
                value={formData.minutes_consumed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    minutes_consumed: e.target.value,
                  }))
                }
                placeholder="Ej. 30, 45, 60"
                required
                sx={{ marginTop: "8px" }}
                inputProps={{
                  min: 1,
                  step: 1,
                }}
                helperText={
                  formData.minutes_consumed
                    ? `Equivale a ${formatMinutes(
                      parseInt(formData.minutes_consumed, 10) || 0
                    )}`
                    : "Ingresa el número de minutos (ej: 15, 30, 45, 60)"
                }
              />
            </Box>

            {/* Descripción del trabajo */}
            <Box>
              <Label sx={{ fontSize: "1rem" }}>Descripción del trabajo</Label>
              <CustomTextArea
                value={formData.work_description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    work_description: e.target.value,
                  }))
                }
                placeholder="Describe brevemente el trabajo realizado..."
                sx={{ marginTop: "8px", minHeight: "100px" }}
              />
            </Box>

            {/* Botones de acción */}
            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{ paddingTop: "24px" }}
            >
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  !formData.minutes_consumed ||
                  parseInt(formData.minutes_consumed, 10) <= 0
                }
              >
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                        border: "1px solid currentColor",
                        borderTop: "1px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                    Guardando...
                  </Box>
                ) : (
                  <>
                    <Save size={16} style={{ marginRight: "8px" }} />
                    {currentTimeLog ? "Actualizar Tiempo" : "Registrar Tiempo"}
                  </>
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </UnifiedModal>
  );
};
